import { MissionAssessmentRequestNestedSelectDto, TechnologyNestedSelectDto } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { TooltipComponent } from '@components/core/tooltip.component';
import { AvatarList } from '@components/shared/avatar-list.component';
import { BlueLabel } from '@components/shared/blue-label.component';
import { TextWithLabel } from '@components/shared/text-with-label.component';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@components/ui/accordion';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { countries } from '@utils/constants';
import { formatDistanceToNow } from 'date-fns';
import { ArrowRight } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ExpandableText } from '../../../shared/expandable-text.component';
import { MISSION_ASSESSMENT_ACCESS_RESTRICTED_MESSAGE } from '../mission-assessment.constants';

type MissionAssessmentCardProps = {
  accessRestricted?: boolean;
  accessRestrictedMessage?: string;
  assessmentId: string;
  assessmentCount?: number;
  isDraft: boolean;
  isRequestWithdrawn?: boolean;
  layout?: 'default' | 'compact';
  request?: MissionAssessmentRequestNestedSelectDto;
  technology: TechnologyNestedSelectDto;
  updatedAt: string;
};

export const MissionAssessmentCard: React.FC<MissionAssessmentCardProps> = (props) => {
  const {
    accessRestricted = false,
    accessRestrictedMessage,
    assessmentCount,
    assessmentId,
    isDraft,
    isRequestWithdrawn = false,
    layout = 'default',
    request,
    technology,
    updatedAt,
  } = props;
  const veteranServices = request?.veteranServices;

  const navigate = useNavigate();

  const [isHovering, setIsHovering] = useState(false);

  const isCompleted = !isDraft;
  const isCompact = layout === 'compact';
  const restrictedMessage = accessRestrictedMessage ?? MISSION_ASSESSMENT_ACCESS_RESTRICTED_MESSAGE;
  const shouldDisableAssessmentNavigation = !isDraft && accessRestricted;

  const buttonText = isDraft
    ? 'Continue Assessment'
    : shouldDisableAssessmentNavigation
      ? 'Preview only'
      : 'View Assessment';
  const buttonLink = isDraft
    ? `/technology/${technology.id}/assessment/${assessmentId}/edit${request?.id ? `?requestId=${request.id}` : ''}`
    : `/technology/${technology.id}?tab=ma&maId=${assessmentId}`;

  const handleNavigate = () => {
    if (shouldDisableAssessmentNavigation) {
      return;
    }

    navigate(buttonLink);
  };

  const veteranCountryNames = useMemo(() => {
    if (!request?.veteranCountriesOfService || request.veteranCountriesOfService.length === 0) {
      return [];
    }

    return request.veteranCountriesOfService
      .map((code) => {
        const country = countries.find((c) => c.code === code);
        return country?.name ?? code;
      })
      .filter(Boolean);
  }, [request?.veteranCountriesOfService]);

  const veteranCountryDisplay = useMemo(() => {
    if (veteranCountryNames.length === 0) {
      return undefined;
    }
    if (veteranCountryNames.length === 1) {
      return veteranCountryNames[0];
    }
    return veteranCountryNames.join(', ');
  }, [veteranCountryNames]);

  const serviceAvatarItems = useMemo(() => {
    if (!veteranServices || veteranServices.length === 0) {
      return [];
    }

    return veteranServices.map((service) => {
      let tooltip = service.name;
      if (service.parentService?.name) {
        const parentCountryName = service.parentService.name;
        const country = countries.find((c) => c.name === parentCountryName);
        const countryCode = country?.code ?? parentCountryName;
        tooltip = `${countryCode} - ${service.name}`;
      }

      return {
        altText: service.name,
        imageUrl: service.slug ? `/images/services/${service.slug}.png` : undefined,
        initialText: service.name,
        tooltip,
      };
    });
  }, [veteranServices]);

  const renderRestrictedBanner = () => {
    if (!shouldDisableAssessmentNavigation) return null;

    return (
      <div className="mb-4 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
        <span aria-hidden>🚫</span>
        <span>{restrictedMessage}</span>
      </div>
    );
  };

  const renderWithdrawnBadge = () => {
    if (!isRequestWithdrawn) return null;

    return (
      <TooltipComponent
        className="text-xs"
        side="top"
        trigger={
          <Badge
            className="shrink-0"
            size="xs"
            variant="destructive"
          >
            Withdrawn
          </Badge>
        }
      >
        The technology owner has cancelled this mission assessment request. You can still view your draft, but it will
        not be submitted.
      </TooltipComponent>
    );
  };

  if (isCompact) {
    return (
      <div
        className="w-full p-3 bg-white border border-gray-200 rounded-lg shadow-sm"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {/* Compact Header */}
        <div className="flex justify-between items-start mb-2">
          <div className="flex-1 pr-2">
            <div className="flex items-center gap-2">
              <Link
                className="text-lg font-medium text-gray-800 line-clamp-2 hover:underline"
                to={`/technology/${technology.id}`}
              >
                {technology.name}
              </Link>
              {renderWithdrawnBadge()}
            </div>
          </div>

          <IconButton
            ariaLabel={buttonText}
            disabled={shouldDisableAssessmentNavigation}
            icon={shouldDisableAssessmentNavigation ? <span aria-hidden>🚫</span> : <ArrowRight />}
            isVisible={isHovering}
            onClick={handleNavigate}
            size="md"
            variant="secondary"
          />
        </div>

        {renderRestrictedBanner()}

        {isCompleted && (
          <Accordion
            className="w-full bg-[#F8F8F8] px-4 py-1 rounded-xl"
            collapsible
            type="single"
          >
            <AccordionItem
              className="border-none"
              value="details"
            >
              <AccordionTrigger className="py-2 hover:no-underline">
                <div className="flex flex-col items-start text-left space-y-2">
                  {request?.use?.name && (
                    <TextWithLabel
                      className="text-sm"
                      label="Use"
                      title={request.use.name}
                    />
                  )}
                  {request?.domain?.name && (
                    <TextWithLabel
                      className="text-sm"
                      label="Domain"
                      title={request.domain.name}
                    />
                  )}
                </div>
              </AccordionTrigger>

              <AccordionContent className="pb-2">
                <div className="h-px bg-gray-200 my-1" />
                <div className="flex flex-row justify-between gap-2 mt-2">
                  {Boolean(assessmentCount) && (
                    <TextWithLabel
                      className="text-sm flex-col-reverse"
                      label="Assessed"
                      title={`${assessmentCount} ${assessmentCount === 1 ? 'time' : 'times'}`}
                    />
                  )}

                  <TextWithLabel
                    className="ml-auto text-sm flex-col-reverse"
                    label="Completed"
                    title={formatDistanceToNow(new Date(updatedAt), { addSuffix: true })}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}

        {/* Fallback for non-completed assessments */}
        {!isCompleted && (
          <div className="flex flex-col gap-2 text-sm text-gray-500">
            {Boolean(assessmentCount) && (
              <div className="flex flex-col">
                <BlueLabel text="Assessed" />
                <span className="text-gray-800">
                  {assessmentCount} {assessmentCount === 1 ? 'time' : 'times'}
                </span>
              </div>
            )}

            {(request?.use?.name || request?.domain?.name) && (
              <>
                <div className="h-px bg-gray-200 my-1" />
                <div className="flex flex-col gap-1">
                  {request?.use?.name && (
                    <TextWithLabel
                      className="text-sm"
                      label="Use"
                      title={request.use.name}
                    />
                  )}
                  {request?.domain?.name && (
                    <TextWithLabel
                      className="text-sm"
                      label="Domain"
                      title={request.domain.name}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-full p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
      {/* Header Section */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Link
              className="text-lg font-medium text-gray-800 truncate pr-2 hover:underline"
              to={`/technology/${technology.id}`}
            >
              {technology.name}
            </Link>
            {renderWithdrawnBadge()}
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4 text-sm text-gray-500 shrink-0">
          {Boolean(assessmentCount) && (
            <div className="text-center sm:text-left">
              <BlueLabel text="Assessed" />
              <p className="text-gray-800 mt-1">
                {assessmentCount} {assessmentCount === 1 ? 'time' : 'times'}
              </p>
            </div>
          )}

          {Boolean(assessmentCount) && request && <div className="hidden sm:block w-px h-8 bg-gray-200" />}

          {request && !isCompleted && (
            <div className="text-right">
              <BlueLabel text="Requested" />
              <p className="text-gray-800 mt-1">
                {formatDistanceToNow(new Date(request.createdAt), { addSuffix: true })}
              </p>
            </div>
          )}

          {isCompleted && (
            <div>
              <BlueLabel text="Completed" />
              <p className="text-gray-800 mt-1">{formatDistanceToNow(new Date(updatedAt), { addSuffix: true })}</p>
            </div>
          )}
        </div>
      </div>

      <div className="h-px bg-gray-200 my-4" />

      {/* Content Section */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-3 md:flex-row md:gap-4 flex-1">
          {request?.use?.name && (
            <TextWithLabel
              className="text-sm"
              label="Use"
              title={request?.use?.name}
            />
          )}
          {request?.domain?.name && (
            <TextWithLabel
              className="text-sm"
              label="Domain"
              title={request.domain?.name}
            />
          )}
        </div>

        <div className="shrink-0 w-full md:w-auto">
          {shouldDisableAssessmentNavigation ? (
            <Button
              className="w-full md:w-auto"
              disabled
              variant="secondary"
            >
              {buttonText}
            </Button>
          ) : (
            <Link
              className="block"
              to={buttonLink}
            >
              <Button
                className="w-full md:w-auto"
                variant="primary"
              >
                {buttonText}
              </Button>
            </Link>
          )}
        </div>
      </div>

      {renderRestrictedBanner()}

      {veteranCountryDisplay && (
        <div className="flex flex-wrap items-stretch gap-4 mt-4">
          <TextWithLabel
            className="text-sm whitespace-nowrap mt-auto"
            label={veteranCountryNames.length > 1 ? 'Target Nations' : 'Target Nation'}
            title={veteranCountryDisplay}
          />
          {serviceAvatarItems.length > 0 && (
            <AvatarList
              items={serviceAvatarItems}
              label={serviceAvatarItems.length > 1 ? 'Services' : 'Service'}
              limit={3}
              size="sm"
            />
          )}
        </div>
      )}

      {!shouldDisableAssessmentNavigation && request?.message && (
        <div className="mt-4">
          <ExpandableText
            className="text-xs text-gray-600 max-w-full"
            text={request.message}
          />
        </div>
      )}
    </div>
  );
};
